import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Check, Zap, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Pricing = ({ token }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plansData, setPlansData] = useState(null);

  useEffect(() => {
    if (token) {
      fetchPlans();
    }
  }, [token]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscription/plans`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setPlansData(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleSelectPlan = async (planId) => {
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setSelectedPlan(planId);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/subscription/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const plans = [
    {
      id: 'individual_monthly',
      name: 'Individual',
      price: '$14.99',
      period: 'month',
      description: 'Perfect for personal finance tracking',
      icon: <Zap className="h-6 w-6" />,
      features: [
        'Unlimited transactions',
        'Unlimited categories',
        'AI-powered categorization',
        'Advanced insights & analytics',
        'Budget tracking',
        'CSV/Excel import',
        'Monthly reports',
      ],
      popular: false,
    },
    {
      id: 'individual_yearly',
      name: 'Individual',
      price: '$149',
      period: 'year',
      description: 'Save 17% with annual billing',
      icon: <Zap className="h-6 w-6" />,
      features: [
        'Unlimited transactions',
        'Unlimited categories',
        'AI-powered categorization',
        'Advanced insights & analytics',
        'Budget tracking',
        'CSV/Excel import',
        'Monthly reports',
      ],
      popular: true,
      savings: 'Save $31/year',
    },
    {
      id: 'family_monthly',
      name: 'Family',
      price: '$20.99',
      period: 'month',
      description: 'Best for household budgeting',
      icon: <Users className="h-6 w-6" />,
      features: [
        'Everything in Individual',
        'Multi-user access',
        'Shared budgets',
        'Family insights',
        'Priority support',
        'Custom categories',
      ],
      popular: false,
    },
    {
      id: 'family_yearly',
      name: 'Family',
      price: '$199',
      period: 'year',
      description: 'Best value for families',
      icon: <Users className="h-6 w-6" />,
      features: [
        'Everything in Individual',
        'Multi-user access',
        'Shared budgets',
        'Family insights',
        'Priority support',
        'Custom categories',
      ],
      popular: false,
      savings: 'Save $53/year',
    },
  ];

  const isFreePromo = plansData?.is_free_period;

  return (
    <div className="min-h-screen bg-background py-20" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-heading font-semibold tracking-tight text-foreground mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl font-body text-muted-foreground max-w-3xl mx-auto">
            Get full control of your finances with our professional-grade tools
          </p>
          
          <div className="mt-6 inline-block p-4 bg-blue-50 border-2 border-blue-500 rounded-lg">
            <p className="text-lg font-heading font-semibold text-blue-900">
              Start with a 14-day free trial
            </p>
            <p className="text-sm font-body text-blue-700 mt-1">
              Try all features free for 2 weeks. No credit card required to start.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-card border-2 rounded-sm shadow-lg p-6 relative ${
                plan.popular ? 'border-brand-gold' : 'border-border'
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-brand-gold text-white px-4 py-1 rounded-sm text-xs font-mono font-semibold uppercase">
                    Best Value
                  </span>
                </div>
              )}
              
              {plan.savings && (
                <div className="absolute -top-3 right-4">
                  <span className="bg-green-500 text-white px-3 py-1 rounded-sm text-xs font-mono font-semibold">
                    {plan.savings}
                  </span>
                </div>
              )}

              <div className="mb-6">
                <div className="h-12 w-12 rounded-sm bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {plan.icon}
                </div>
                <h3 className="text-2xl font-heading font-semibold text-foreground mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm font-body text-muted-foreground mb-4">
                  {plan.description}
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-heading font-bold text-foreground">
                    {plan.price}
                  </span>
                  <span className="text-sm font-body text-muted-foreground">/{plan.period}</span>
                </div>
              </div>

              <Button
                onClick={() => handleSelectPlan(plan.id)}
                disabled={loading || isFreePromo}
                className="w-full mb-6"
                variant={plan.popular ? 'default' : 'outline'}
                data-testid={`select-plan-${plan.id}`}
              >
                {loading && selectedPlan === plan.id ? 'Processing...' : isFreePromo ? 'Free Now' : 'Select Plan'}
              </Button>

              <div className="space-y-3">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-body text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm font-body text-muted-foreground">
            All plans include 30-day money-back guarantee • Cancel anytime • Secure payment via Stripe
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
